import React, { useEffect, useState } from 'react';
import fr from 'facet-rest';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import {
  FDContentContainer,
  FDPrimaryActionButton, FDSecondaryActionButton, FDStack,
} from '@facet-wealth/design';
import { RetirementWithdrawalStrategyTable } from 'Organisms/retirementWithdrawalStrategyTable';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import UpdatedScenarioChartWithEmphasizedItems from 'Organisms/UpdatedScenarioChartWithEmphasizedItems';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import DialogTitle from 'Views/Page/Content/Modal/Components/PageModalTitle';
import DataContainer from 'Components/UI/DataContainer';
import { MUTATION_COMPARISON_COLORS } from 'Organisms/retirementWithdrawalStrategyTable/consts';

const mutationIDToDataSetID = (mutationID, chartSchemaData) => {
  // A mutation ID is something like "proRata" whereas a chart data set ID is something like "proRata_xyz".
  // I want to just search by the prefix, but that could lead to more than 1 result - for example, if one option is "proRata" and another is "proRata2.0", and I search for "proRata", I'd see both.
  // Let's search by the prefix, then sort by the number of characters in the ID and pick the lowest one (which avoids the pitfall mentioned prior)
  const matches = chartSchemaData.filter(schema => schema.id.startsWith(mutationID));
  matches.sort((a, b) => a.id.length - b.id.length);
  return matches[0].id;
};

const adjustChartColors = (chartResource, selectedMutationID, comparingMutationIDs) => {
  const selectedMutationDataSetID = mutationIDToDataSetID(selectedMutationID, chartResource.attr('dataSchema').data);
  const comparingMutationDataSetIDs = comparingMutationIDs?.map(id => mutationIDToDataSetID(id, chartResource.attr('dataSchema').data));
  const newData = chartResource.attr('dataSchema').data.map(datum => {
    let color;
    if (!comparingMutationDataSetIDs && datum.id === selectedMutationDataSetID) {
      [color] = MUTATION_COMPARISON_COLORS;
    }
    if (comparingMutationDataSetIDs && comparingMutationDataSetIDs.includes(datum.id)) {
      color = MUTATION_COMPARISON_COLORS[comparingMutationDataSetIDs.indexOf(datum.id)];
    }
    return { ...datum, attributes: { ...datum.attributes, color } };
  });
  // Create a copy so that we have something new to reference, and react will pick up on the change
  const copy = chartResource.copy();
  copy.attr('dataSchema', { ...chartResource.attr('dataSchema'), data: newData });
  return copy;
};

export function FacetPlanRetirementWithdrawalLayout() {
  const {
    resource, onSubmit, saving, handleChange,
  } = useMetaLayoutContext();
  const { clearModalCollection, loadModalCollectionByLink } = useModalContext();
  const { displayErrorMessage } = useSnackbarContext();
  const { refreshPageContentCollection } = usePageContentContext();
  const [mutationsToCompare, setMutationsToCompare] = useState(undefined);
  const mutations = resource.attr('mutations').resources().list();
  const showComparison = mutationsToCompare && mutationsToCompare.length > 0;

  // Start off not setting the chart resource because adjustChartColors is a bit expense to run on every re-render
  const [chartResource, setChartResource] = useState();
  const chartSchemaData = chartResource?.attr('dataSchema')?.data ?? [];

  // If the comparison changes or the chosen item changes, or we get a chart, then chart colors need to be adjusted
  const chosen = resource.attr('retirementWithdrawalStrategy');
  const hasChart = !!resource.attr('chart').resources().head();
  useEffect(() => {
    if (hasChart) {
      setChartResource(adjustChartColors(resource.attr('chart').resources().head(), resource.attr('retirementWithdrawalStrategy'), mutationsToCompare));
    }
  }, [chosen, mutationsToCompare, hasChart]);

  const toggleComparing = () => setMutationsToCompare(isComparing => {
    return isComparing ? undefined : [resource.attr('retirementWithdrawalStrategy') ?? mutations[0]];
  });

  // If there's a navigate relationship, we need to load it into the modal
  useEffect(() => {
    resource.withRelationship(
      fr.relationships.navigate.name,
      (rel) => loadModalCollectionByLink(rel.link('related').url()),
      () => {},
    );
  }, [resource]);
  // If there's a navigate relationship, we need to show a spinner
  return resource.withRelationship(
    fr.relationships.navigate.name,
    () => (
      <FDStack direction="column" flex={1}>
        <DialogTitle>
          <MetaControlField attr="title" />
        </DialogTitle>
        <FDStack direction="column" flex={1} justifyContent="center">
          <DataContainer
            loading
            backgroundColor="none"
            LoadingIndicatorProps={{ loadingText: resource.attr('loadingText') }}
          />
        </FDStack>
      </FDStack>
    ),
    () => (
      <FDContentContainer maxWidth="xl">
        <FDStack direction="column" spacing={2}>
          <DialogTitle>
            <MetaControlField attr="title" />
          </DialogTitle>
          <FDStack
            direction={'column'}
            spacing={2}
            justifyContent={'center'}
            alignItems={'center'}
            width={'100%'}
            paddingX={4}
            height={'33vh'}
            minHeight={'300px'}
          >
            {chartResource ? (
              <UpdatedScenarioChartWithEmphasizedItems
                selectedDataSetId={mutationIDToDataSetID(resource.attr('retirementWithdrawalStrategy'), chartSchemaData)}
                chartDataSourceIdsToShow={
                showComparison
                  ? mutationsToCompare.map(mutationID => mutationIDToDataSetID(mutationID, chartSchemaData))
                  : mutationIDToDataSetID(resource.attr('retirementWithdrawalStrategy'), chartSchemaData)
              }
                chartDataResource={chartResource}
                showLegend
                yAxisLabel={resource.attr('yAxisLabel')}
                xAxisLabel={resource.attr('xAxisLabel')}
              />
            ) : null}
          </FDStack>
          <RetirementWithdrawalStrategyTable
            mutations={mutations}
            chosen={resource.attr('retirementWithdrawalStrategy')}
            onChoose={(option) => handleChange('retirementWithdrawalStrategy', option)}
            showComparison={showComparison}
            mutationsToCompare={mutationsToCompare}
            setMutationsToCompare={setMutationsToCompare}
          />
          <FDStack direction="row" spacing={2} justifyContent="flex-start">
            {showComparison ? null : (
              <FDPrimaryActionButton
                label="Save and close"
                loading={saving}
                onClick={async () => {
                  try {
                    await onSubmit({ rejectOnError: true });
                    clearModalCollection();
                    refreshPageContentCollection();
                  } catch (err) {
                    displayErrorMessage(err, err?.errors || 'Error saving');
                  }
                }}
              />
            )}
            <FDSecondaryActionButton
              label={showComparison ? 'Exit comparison view' : 'Compare graphs'}
              onClick={toggleComparing}
            />
          </FDStack>
        </FDStack>
      </FDContentContainer>
    ),
  );
}
